import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowLeft, CalendarDays, Clock, User } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export default function ArticleView() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 90, damping: 25 });

  useEffect(() => {
    api
      .get(`/content/articles/${id}`)
      .then((r) => setArticle(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="article-not-found">
        <h1 className="font-display text-3xl font-bold">Το άρθρο δεν βρέθηκε</h1>
        <Link to="/arthra" className="text-terracottaD font-medium inline-flex items-center gap-2 mt-6">
          <ArrowLeft className="w-4 h-4" /> Πίσω στα άρθρα
        </Link>
      </div>
    );
  }

  if (!article) {
    return <div className="pt-40 pb-32 text-center font-mono2 text-sm text-slate2">Φόρτωση…</div>;
  }

  return (
    <div data-testid="article-view-page">
      <motion.div className="fixed top-0 inset-x-0 h-1 bg-terracotta origin-left z-[60]" style={{ scaleX: progress }} />
      <article className="pt-32 sm:pt-40 pb-24 sm:pb-32 bg-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Reveal>
            <Link to="/arthra" data-testid="article-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-slate2 hover:text-terracottaD transition-colors duration-300">
              <ArrowLeft className="w-4 h-4" /> Όλα τα άρθρα
            </Link>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="font-mono2 text-[11px] tracking-[0.25em] uppercase text-terracottaD mt-10">{article.category}</p>
          </Reveal>
          <Reveal delay={0.14}>
            <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.12] mt-4">{article.title}</h1>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex flex-wrap items-center gap-5 mt-6 font-mono2 text-[11px] tracking-[0.15em] uppercase text-ink/50">
              {article.author && (
                <span className="inline-flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" /> {article.author}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="w-3.5 h-3.5" /> {article.date}
              </span>
              {article.read_time && (
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> {article.read_time}
                </span>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.26}>
            <p className="font-display text-xl sm:text-2xl leading-relaxed text-slate2 mt-10 border-l-2 border-terracotta pl-6 italic">
              {article.excerpt}
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 space-y-6 text-base sm:text-lg leading-relaxed text-ink/85">
              {(article.content || "").split(/\n\s*\n/).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </article>
    </div>
  );
}
